import gql from "graphql-tag";

import type {GqlFetchFn} from "@repo/data-gql";
import {gqlOp, type GqlOperation} from "@repo/data-gql-core";
import Log, {Errors, KAError} from "@repo/logging";

import type {
    DismissItemMutationType,
    UserHasDismissedQueryType,
} from "./__graphql-types__/index.ts";

export type Expiration = "never" | number;

export const DISMISSIBLE_QUERY: GqlOperation<
    UserHasDismissedQueryType["response"],
    UserHasDismissedQueryType["variables"]
> = gqlOp(gql`
    query UserHasDismissedQuery($itemName: String!) {
        dismissedItem(itemName: $itemName) {
            id
            isDismissed
        }
    }
`);

export const DISMISS_UNTIL_MUTATION: GqlOperation<
    DismissItemMutationType["response"],
    DismissItemMutationType["variables"]
> = gqlOp(gql`
    mutation DismissItemMutation($itemName: String!, $expires: DateTime) {
        dismissItem(itemName: $itemName, expires: $expires) {
            error {
                code
            }
            dismissedItem {
                id
                isDismissed
            }
        }
    }
`);

const dismissUntilDate = (
    gqlFetch: GqlFetchFn,
    dismissKey: string,
    expires: Date,
) => {
    const expiresString = expires.toISOString();

    gqlFetch(DISMISS_UNTIL_MUTATION, {
        variables: {
            itemName: dismissKey,
            expires: expiresString,
        },
    })
        .then((response) => {
            if (response.dismissItem?.error != null) {
                const {code} = response.dismissItem.error;
                throw new KAError(code, Errors.GraphqlResponse, {
                    metadata: {errorCode: code},
                });
            }
            return response;
        })
        .catch((e) => {
            Log.error(
                "Failed to mutate dismiss state",
                Errors.TransientKhanService,
                {
                    cause: e,
                    sentryData: {
                        contexts: {
                            extras: {
                                dismissKey,
                                expiresString,
                                errorCode: e.errorCode,
                            },
                        },
                    },
                },
            );
        });
};

const expirationToDate = (expiration: Expiration) => {
    const distantFuture = new Date(3000, 6, 1);

    if (expiration === "never") {
        // A expiration date is required for dismissed items (for garbage
        // collection purposes), so if a dismissed item should never expire, we
        // set the expiration to a date in the distant future.
        return distantFuture;
    }
    const now = new Date().getTime();
    const days = 1000 * 60 * 60 * 24;
    return new Date(now + expiration * days);
};

export const isDismissed = (
    gqlFetch: GqlFetchFn,
    dismissKey: string,
): Promise<boolean> => {
    return gqlFetch(DISMISSIBLE_QUERY, {
        variables: {itemName: dismissKey},
    })
        .then((response) => Boolean(response.dismissedItem?.isDismissed))
        .catch((err) => {
            Log.log(
                `Failed to query dismiss state for dismissKey: ${dismissKey}`,
            );
            return true;
        });
};

export const dismiss = (
    gqlFetch: GqlFetchFn,
    dismissKey: string,
    expiresInDays: Expiration,
): void =>
    dismissUntilDate(gqlFetch, dismissKey, expirationToDate(expiresInDays));
